import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { circlePolygon, buildProgress, arcLine, offsetPoint } from '../lib/geo.js';
import { useTheme } from '../lib/theme.jsx';

// CARTO's open GL styles: vector tiles, no API key, no vendor account.
const STYLES = {
  dark:  'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
  light: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
};

const SRC = 'fields';
const FX = 'fields-fx';

/**
 * The visual language of a field, keyed by kind.
 *
 * Today a field's kind is its intensity. When other account types can raise
 * their own kinds of field, they get an entry here (colour, name, signature
 * effect) and nothing else in this file needs to change.
 *
 *   colour            fill, outline, label and effects
 *   fx(f, t, R, out)  the kind's signature animation while active; pushes
 *                     effect features into `out` (see toEffects)
 */
const FIELD_KINDS = {
  spark: {
    name: 'Spark', color: '#d2a53c',
    // quick, restless motes flickering just inside the edge
    fx(f, t, R, out, lng, lat) {
      for (let i = 0; i < 6; i++) {
        const a = (Math.sin(t * 6 + i * 2.1) + 1) / 2;
        const ang = i * 1.047 + Math.sin(t * 0.7 + i) * 0.4;
        out.push(mote(offsetPoint(lng, lat, R * (0.35 + 0.5 * ((i * 0.37 + t * 0.05) % 1)), ang), this.color, 0.2 + a * 0.7, 2 + a * 2));
      }
    },
  },
  glow: {
    name: 'Glow', color: '#c2785a',
    // a soft halo breathing slowly around the field
    fx(f, t, R, out, lng, lat) {
      const b = (Math.sin(t * 0.9) + 1) / 2;
      out.push({ type: 'Feature', properties: { kind: 'halo', color: this.color, alpha: 0.06 + b * 0.1 },
                 geometry: circlePolygon(lng, lat, R * (1.06 + b * 0.1), 40) });
    },
  },
  beacon: {
    name: 'Beacon', color: '#3f8f7f',
    // a lighthouse sweep turning steadily, with a pulse ring every few seconds
    fx(f, t, R, out, lng, lat) {
      const from = (t * 70) % 360;
      out.push({ type: 'Feature', properties: { kind: 'halo', color: this.color, alpha: 0.28 },
                 geometry: sector(lng, lat, R * 0.98, from, from + 55) });
      out.push({ type: 'Feature', properties: { kind: 'halo', color: this.color, alpha: 0.12 },
                 geometry: sector(lng, lat, R * 0.98, from - 40, from) });
      const ph = (t / 3) % 1;
      out.push({ type: 'Feature', properties: { kind: 'ring', color: this.color, alpha: (1 - ph) * 0.6 },
                 geometry: circlePolygon(lng, lat, R * (0.2 + 0.85 * ph), 40) });
      out.push(mote([lng, lat], this.color, 0.9, 4 + 2 * Math.abs(Math.sin(t * 2))));
    },
  },
  sanctuary: {
    name: 'Sanctuary', color: '#7d63a6',
    // still, layered rings and a slow orbit — nothing hurries here
    fx(f, t, R, out, lng, lat) {
      [0.45, 0.7, 0.92].forEach((k, i) => {
        const a = 0.25 + 0.2 * ((Math.sin(t * 0.6 + i * 1.3) + 1) / 2);
        out.push({ type: 'Feature', properties: { kind: 'ring', color: this.color, alpha: a },
                   geometry: circlePolygon(lng, lat, R * k, 48) });
      });
      for (let i = 0; i < 8; i++) {
        out.push(mote(offsetPoint(lng, lat, R * 0.82, (i / 8) * 2 * Math.PI + t * 0.12), this.color, 0.7, 2.5));
      }
    },
  },
};
const BUILDING = '#c4622d';    // ember — forming
const EXPIRED  = '#6b6055';
const MINE     = '#c9a227';    // brass — the ring that says "yours"
const ENDING   = '#b0483f';    // rust — the arc's colour in the last stretch

const kindOf = (f) => FIELD_KINDS[f.intensity] ?? FIELD_KINDS.glow;

function mote(coordinates, color, alpha, size) {
  return { type: 'Feature', properties: { kind: 'mote', color, alpha, size }, geometry: { type: 'Point', coordinates } };
}

/** A pie slice from `fromDeg` to `toDeg` (compass degrees). */
function sector(lng, lat, r, fromDeg, toDeg) {
  const arc = arcLine(lng, lat, r, fromDeg, toDeg, 48).coordinates;
  return { type: 'Polygon', coordinates: [[[lng, lat], ...arc, [lng, lat]]] };
}

function remainingLabel(ms) {
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
}

/** Fraction of the field's active life still ahead, 0..1. */
function remainingFraction(f, now) {
  const start = new Date(f.activates_at).getTime();
  const end = new Date(f.expires_at).getTime();
  if (!(end > start)) return 0;
  return Math.min(1, Math.max(0, (end - now) / (end - start)));
}

/** Static geometry: the field itself and its label. Repainted on data change. */
function toFeatures(fields, now) {
  const out = [];
  for (const f of fields) {
    const kind = kindOf(f);
    const building = f.status === 'building';
    const expired = f.status === 'expired';
    const color = building ? BUILDING : expired ? EXPIRED : kind.color;
    const r = f.radius_m * (building ? Math.max(0.08, buildProgress(f, now)) : 1);
    const props = {
      kind: 'field', id: f.id, status: f.status, color,
      isMine: Boolean(f.is_mine), isPublic: Boolean(f.is_public), radius: f.radius_m,
    };
    out.push({ type: 'Feature', id: f.id, properties: props, geometry: circlePolygon(f.longitude, f.latitude, r) });
    if (!expired) {
      const text = building
        ? `${kind.name} · forming`
        : `${kind.name} · ${remainingLabel(new Date(f.expires_at).getTime() - now)}`;
      out.push({
        type: 'Feature',
        properties: { kind: 'label', color, text, private: !f.is_public },
        // anchored just below the circle so it never covers the centre
        geometry: { type: 'Point', coordinates: offsetPoint(f.longitude, f.latitude, f.radius_m * 1.18, -Math.PI / 2) },
      });
    }
  }
  return out;
}

/**
 * Animated geometry, rebuilt every frame while anything on the map is alive.
 *
 * Building: dashed ghost of the final size, sonar rings breathing outward
 * through the part already formed, motes drawn in toward a pulsing core, a
 * progress arc.  Active: the kind's signature effect plus a clock arc showing
 * the life left (rust when nearly over).  Connected: a slow orbit of motes.
 * Everything is ground geometry, so it scales with the map.
 */
function toEffects(fields, now, checkedInId) {
  const out = [];
  const t = now / 1000;
  for (const f of fields) {
    const { longitude: lng, latitude: lat, radius_m: R } = f;
    if (f.status === 'building') {
      const p = buildProgress(f, now);
      const cur = R * Math.max(0.08, p);
      const color = BUILDING;
      out.push({ type: 'Feature', properties: { kind: 'ghost', color }, geometry: circlePolygon(lng, lat, R, 48) });
      if (p > 0.005) out.push({ type: 'Feature', properties: { kind: 'arc', color, width: 4 },
                                geometry: arcLine(lng, lat, R * 1.06, 0, 360 * p) });
      for (let i = 0; i < 3; i++) {
        const ph = ((t / 2.4) + i / 3) % 1;
        out.push({ type: 'Feature', properties: { kind: 'ring', color, alpha: (1 - ph) * 0.7 },
                   geometry: circlePolygon(lng, lat, Math.max(1, cur * ph), 40) });
      }
      for (let i = 0; i < 14; i++) {
        const ph = ((t / 3.2) + (i * 0.618)) % 1;
        const ang = i * 0.449 * Math.PI + t * 0.15;
        out.push(mote(offsetPoint(lng, lat, R * (1.35 - 1.35 * ph), ang), color, 0.25 + 0.7 * ph, 2 + 3 * ph));
      }
      const beat = (Math.sin(t * 3) + 1) / 2;
      out.push({ type: 'Feature', properties: { kind: 'core', color, alpha: 0.55 + beat * 0.4, size: 6 + beat * 6 },
                 geometry: { type: 'Point', coordinates: [lng, lat] } });
    } else if (f.status === 'active') {
      const kind = kindOf(f);
      kind.fx(f, t, R, out, lng, lat);
      // the clock: life left, from north, clockwise; rust once it is nearly spent
      const left = remainingFraction(f, now);
      if (left > 0.002) {
        out.push({ type: 'Feature', properties: { kind: 'arc', color: left < 0.1 ? ENDING : kind.color, width: 3 },
                   geometry: arcLine(lng, lat, R * 1.08, 0, 360 * left) });
      }
      if (f.is_mine) {
        out.push({ type: 'Feature', properties: { kind: 'mine', color: MINE },
                   geometry: circlePolygon(lng, lat, R * 1.14, 48) });
      }
      if (f.id === checkedInId) {
        for (let i = 0; i < 10; i++) {
          const wob = R * (0.88 + 0.08 * Math.sin(t * 1.7 + i));
          out.push(mote(offsetPoint(lng, lat, wob, (i / 10) * 2 * Math.PI + t * 0.35), MINE, 0.85, 3));
        }
      }
    }
  }
  return out;
}

/** Sources and layers, shared by first load and every restyle. */
function ensureLayers(m) {
  if (m.getSource(SRC)) return;
  m.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  m.addSource(FX,  { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  const isField = ['==', ['get', 'kind'], 'field'];
  const fx = (k) => ['==', ['get', 'kind'], k];

  m.addLayer({ id: 'fields-fill', type: 'fill', source: SRC, filter: isField,
    paint: { 'fill-color': ['get', 'color'],
             'fill-opacity': ['case', ['==', ['get', 'status'], 'expired'], 0.07, 0.18] } });
  // effects sit above the fill and below the outlines
  m.addLayer({ id: 'fx-halo', type: 'fill', source: FX, filter: fx('halo'),
    paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['get', 'alpha'] } });
  m.addLayer({ id: 'fx-ghost', type: 'line', source: FX, filter: fx('ghost'),
    paint: { 'line-color': ['get', 'color'], 'line-width': 1.5, 'line-opacity': 0.55, 'line-dasharray': [2, 3] } });
  m.addLayer({ id: 'fx-ring', type: 'line', source: FX, filter: fx('ring'),
    paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-opacity': ['get', 'alpha'], 'line-blur': 1 } });
  // outlines: public solid, private dashed (dasharray cannot be data-driven, hence two layers)
  m.addLayer({ id: 'fields-line-public', type: 'line', source: SRC, filter: ['all', isField, ['get', 'isPublic']],
    paint: { 'line-color': ['get', 'color'], 'line-width': ['case', ['==', ['get', 'status'], 'building'], 3, 2],
             'line-opacity': ['case', ['==', ['get', 'status'], 'expired'], 0.35, 0.9] } });
  m.addLayer({ id: 'fields-line-private', type: 'line', source: SRC, filter: ['all', isField, ['!', ['get', 'isPublic']]],
    paint: { 'line-color': ['get', 'color'], 'line-width': ['case', ['==', ['get', 'status'], 'building'], 3, 2],
             'line-opacity': ['case', ['==', ['get', 'status'], 'expired'], 0.35, 0.9], 'line-dasharray': [1.6, 1.2] } });
  m.addLayer({ id: 'fx-mine', type: 'line', source: FX, filter: fx('mine'),
    paint: { 'line-color': ['get', 'color'], 'line-width': 1.2, 'line-opacity': 0.9 } });
  m.addLayer({ id: 'fx-arc', type: 'line', source: FX, filter: fx('arc'),
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': ['get', 'color'], 'line-width': ['get', 'width'], 'line-opacity': 0.95 } });
  m.addLayer({ id: 'fx-mote', type: 'circle', source: FX, filter: fx('mote'),
    paint: { 'circle-color': ['get', 'color'], 'circle-radius': ['get', 'size'], 'circle-opacity': ['get', 'alpha'],
             'circle-blur': 0.6, 'circle-pitch-alignment': 'map' } });
  m.addLayer({ id: 'fx-core', type: 'circle', source: FX, filter: fx('core'),
    paint: { 'circle-color': ['get', 'color'], 'circle-radius': ['get', 'size'], 'circle-opacity': ['get', 'alpha'],
             'circle-blur': 0.9, 'circle-pitch-alignment': 'map' } });
  m.addLayer({ id: 'fields-label', type: 'symbol', source: SRC, filter: ['==', ['get', 'kind'], 'label'],
    layout: {
      // plain text only: emoji glyph ranges are not served by the basemap's font host
      'text-field': ['case', ['get', 'private'], ['concat', ['get', 'text'], ' · private'], ['get', 'text']],
      'text-font': ['Open Sans Bold', 'Noto Sans Regular'],
      'text-size': 11, 'text-anchor': 'top', 'text-letter-spacing': 0.05,
      'text-allow-overlap': true, 'text-ignore-placement': true,
    },
    paint: { 'text-color': ['get', 'color'], 'text-halo-color': 'rgba(245,240,232,0.9)', 'text-halo-width': 1.4 } });
}

const FRAME_MS = 1000 / 30;   // effects at 30 fps — plenty for slow ambient motion, kinder to batteries

export default function EnergyMap({ fields, center, focus, userPosition, checkedInId, onSelectField }) {
  const holder = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const { theme } = useTheme();
  const ready = useRef(false);
  // setStyle() tears down our source/layers; the restyle handler needs the
  // current fields to repaint them, and effects alone would race with it.
  const latest = useRef(fields);
  latest.current = fields;

  // ---- create once
  useEffect(() => {
    if (map.current || !holder.current) return;
    map.current = new maplibregl.Map({
      container: holder.current,
      style: STYLES[theme] ?? STYLES.dark,
      center: [center?.lng ?? 55.26, center?.lat ?? 25.23],
      zoom: 17,               // fields are 15–50 m; any wider and they are specks
      pitch: 45,             // gamified tilt rather than a flat road map
      bearing: -15,
      attributionControl: { compact: true },
    });
    const m = map.current;
    m.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');

    m.on('load', () => {
      ready.current = true;
      ensureLayers(m);
      // The field list often arrives before the style has loaded; the draw
      // effect skipped it then, so paint what we have now or it stays blank
      // until the next data change.
      m.getSource(SRC).setData({ type: 'FeatureCollection', features: toFeatures(latest.current, Date.now()) });
      m.on('click', 'fields-fill', (e) => {
        const f = e.features?.[0];
        if (f) onSelectField?.(f.properties.id);
      });
      m.on('mouseenter', 'fields-fill', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'fields-fill', () => { m.getCanvas().style.cursor = ''; });
    });

    return () => { m.remove(); map.current = null; ready.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- restyle on theme change (re-adding layers, since setStyle drops them)
  useEffect(() => {
    const m = map.current;
    if (!m || !ready.current) return;
    ready.current = false;
    m.setStyle(STYLES[theme] ?? STYLES.dark);
    m.once('styledata', () => {
      ensureLayers(m);
      ready.current = true;
      // Repaint immediately: otherwise fields stay invisible until the next
      // data change, which made every theme toggle look like they vanished.
      m.getSource(SRC).setData({
        type: 'FeatureCollection', features: toFeatures(latest.current, Date.now()),
      });
    });
  }, [theme]);

  // ---- geometry + effects: a frame loop while anything is building or
  //      active (labels tick down, arcs shrink, signatures move)
  useEffect(() => {
    let frame, last = 0, labelAt = 0;
    const draw = (ts = performance.now()) => {
      const m = map.current;
      const alive = fields.some((f) => f.status === 'building' || f.status === 'active');
      if (!alive) { paint(m, fields, true); return; }
      frame = requestAnimationFrame(draw);
      if (ts - last < FRAME_MS || document.hidden) return;
      last = ts;
      // the static layer only needs refreshing as labels change (every 20 s)
      // and while something is still growing
      const growing = fields.some((f) => f.status === 'building');
      paint(m, fields, growing || ts - labelAt > 20_000);
      if (growing || ts - labelAt > 20_000) labelAt = ts;
    };
    const paint = (m, list, statics) => {
      if (!ready.current || !m?.getSource(SRC)) return;
      const now = Date.now();
      if (statics) m.getSource(SRC).setData({ type: 'FeatureCollection', features: toFeatures(list, now) });
      m.getSource(FX).setData({ type: 'FeatureCollection', features: toEffects(list, now, checkedInId) });
    };
    paint(map.current, fields, true);
    draw();
    return () => cancelAnimationFrame(frame);
  }, [fields, theme, checkedInId]);

  // ---- recentre
  useEffect(() => {
    if (map.current && center) {
      map.current.easeTo({ center: [center.lng, center.lat], duration: 800 });
    }
  }, [center?.lat, center?.lng]);

  // ---- focus a field (created or selected): fly in close enough to watch it
  useEffect(() => {
    if (map.current && focus) {
      map.current.easeTo({ center: [focus.lng, focus.lat], zoom: focus.zoom ?? 17.5, duration: 900 });
    }
  }, [focus?.lat, focus?.lng, focus?.zoom, focus?.key]);

  // ---- the "you are here" marker
  useEffect(() => {
    const m = map.current;
    if (!m || !userPosition) return;
    if (!marker.current) {
      const el = document.createElement('div');
      el.style.cssText =
        'width:14px;height:14px;border-radius:50%;background:#6cbfa9;' +
        'box-shadow:0 0 0 5px rgba(108,191,169,0.18),0 0 16px rgba(108,191,169,0.7);' +
        'border:1.5px solid rgba(242,236,224,0.9);pointer-events:none';   // taps go to the field beneath
      marker.current = new maplibregl.Marker({ element: el });
    }
    marker.current.setLngLat([userPosition.lng, userPosition.lat]).addTo(m);
  }, [userPosition?.lat, userPosition?.lng]);

  // Sizing is inline, not utility classes: maplibre-gl.css declares
  // `.maplibregl-map { position: relative }`, which beats Tailwind's `absolute`
  // and collapses an inset-0 container to zero height.
  return (
    <div
      ref={holder}
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        background: 'var(--ground-2)',
      }}
    />
  );
}
